import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Icon } from '@iconify/react/dist/iconify.js';

const ItAssetDashboardLayer = () => {
  const [loading, setLoading] = useState(true);
  const [itAssets, setItAssets] = useState([]);

  useEffect(() => {
    const fetchItAssets = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem("authToken");

        if (!apiUrl || !token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${apiUrl}/it-assets?per_page=1000`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const json = await response.json();

        if (json?.success && Array.isArray(json.data)) {
          setItAssets(json.data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load IT asset dashboard data", e);
      } finally {
        setLoading(false);
      }
    };

    fetchItAssets();
  }, []);

  const metrics = useMemo(() => {
    const totalAssets = itAssets.length;
    
    const byCategory = new Map();
    const byCondition = new Map();
    const byDepartment = new Map();
    const byLocation = new Map();

    itAssets.forEach((asset) => {
      // Count by Category
      const category = asset.category || "Unknown";
      byCategory.set(category, (byCategory.get(category) || 0) + 1);

      // Count by Condition
      const condition = asset.condition || "Unknown";
      byCondition.set(condition, (byCondition.get(condition) || 0) + 1);

      // Count by Department
      const department = asset.department || "Unknown";
      byDepartment.set(department, (byDepartment.get(department) || 0) + 1);

      // Count by Location
      const location = asset.location || "Unknown";
      byLocation.set(location, (byLocation.get(location) || 0) + 1);
    });

    // Get latest IT assets (sorted by created_at descending, limit to 10)
    const latestAssets = [...itAssets]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10);

    return {
      totalAssets,
      byCategory: {
        categories: Array.from(byCategory.keys()),
        series: Array.from(byCategory.values()),
      },
      byCondition: {
        categories: Array.from(byCondition.keys()),
        series: Array.from(byCondition.values()),
      },
      byDepartment: {
        categories: Array.from(byDepartment.keys()),
        series: Array.from(byDepartment.values()),
      },
      byLocation: {
        categories: Array.from(byLocation.keys()),
        series: Array.from(byLocation.values()),
      },
      latestAssets,
    };
  }, [itAssets]);

  const getCategoryColor = (index) => {
    const colors = ["#4189dd", "#97cbff", "#095593", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6"];
    return colors[index % colors.length];
  };

  const getConditionColor = (condition) => {
    const colorMap = {
      excellent: "#22C55E", // Green
      good: "#4189dd",      // Dusty Blue
      fair: "#F59E0B",      // Orange
      poor: "#EF4444",      // Red
      damaged: "#6B7280",   // Gray
    };
    return colorMap[condition] || "#4189dd";
  };

  const categoryChartOptions = {
    chart: { type: "pie", toolbar: { show: false } },
    labels: metrics.byCategory.categories,
    colors: metrics.byCategory.categories.map((_, index) => getCategoryColor(index)),
    legend: { position: "bottom" },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` },
  };

  const conditionChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    xaxis: { categories: metrics.byCondition.categories },
    plotOptions: { bar: { distributed: true, borderRadius: 4 } },
    dataLabels: { enabled: false },
    colors: metrics.byCondition.categories.map((cat) => getConditionColor(cat)),
    legend: { show: false },
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getConditionBadge = (condition) => {
    const badges = {
      excellent: 'bg-success-focus text-success-600 border border-success-main',
      good: 'bg-info-focus text-info-600 border border-info-main',
      fair: 'bg-warning-focus text-warning-600 border border-warning-main',
      poor: 'bg-danger-focus text-danger-600 border border-danger-main',
      damaged: 'bg-neutral-200 text-neutral-600 border border-neutral-400'
    };
    return badges[condition] || badges.good;
  };

  return (
    <div className='row gy-4'>
      {/* KPI Cards */}
      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
              <div>
                <span className='mb-2 fw-medium text-secondary-light text-sm d-block'>
                  Total IT Assets
                </span>
                <h3 className='mb-0 text-primary-600 text-2xl fw-semibold'>
                  {metrics.totalAssets}
                </h3>
              </div>
              <div className='w-50-px h-50-px d-flex justify-content-center align-items-center rounded-circle bg-primary-50 text-primary-600'>
                <Icon icon='mdi:laptop' className='text-2xl' />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
              <div>
                <span className='mb-2 fw-medium text-secondary-light text-sm d-block'>
                  Categories
                </span>
                <h3 className='mb-0 text-info-600 text-2xl fw-semibold'>
                  {metrics.byCategory.categories.length}
                </h3>
              </div>
              <div className='w-50-px h-50-px d-flex justify-content-center align-items-center rounded-circle bg-info-50 text-info-600'>
                <Icon icon='solar:tag-linear' className='text-2xl' />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
              <div>
                <span className='mb-2 fw-medium text-secondary-light text-sm d-block'>
                  Departments
                </span>
                <h3 className='mb-0 text-success-600 text-2xl fw-semibold'>
                  {metrics.byDepartment.categories.length}
                </h3>
              </div>
              <div className='w-50-px h-50-px d-flex justify-content-center align-items-center rounded-circle bg-success-50 text-success-600'>
                <Icon icon='solar:buildings-2-linear' className='text-2xl' />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-1'>
              <div>
                <span className='mb-2 fw-medium text-secondary-light text-sm d-block'>
                  Locations
                </span>
                <h3 className='mb-0 text-warning-600 text-2xl fw-semibold'>
                  {metrics.byLocation.categories.length}
                </h3>
              </div>
              <div className='w-50-px h-50-px d-flex justify-content-center align-items-center rounded-circle bg-warning-50 text-warning-600'>
                <Icon icon='solar:map-point-linear' className='text-2xl' />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className='col-xxl-6 col-md-12'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <h6 className='mb-20 fw-bold text-lg'>IT Assets by Category</h6>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : metrics.byCategory.series.length > 0 ? (
              <ReactApexChart
                options={categoryChartOptions}
                series={metrics.byCategory.series}
                type='pie'
                height={360}
              />
            ) : (
              <div className='text-center py-5 text-secondary-light'>No IT asset data available</div>
            )}
          </div>
        </div>
      </div>

      <div className='col-xxl-6 col-md-12'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <h6 className='mb-20 fw-bold text-lg'>IT Assets by Condition</h6>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : metrics.byCondition.series.length > 0 ? (
              <ReactApexChart
                options={conditionChartOptions}
                series={[{ data: metrics.byCondition.series }]}
                type='bar'
                height={360}
              />
            ) : (
              <div className='text-center py-5 text-secondary-light'>No IT asset data available</div>
            )}
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card h-100 shadow-sm radius-12'>
          <div className='card-body p-24'>
            <h6 className='mb-20 fw-bold text-lg'>Latest IT Assets</h6>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : metrics.latestAssets.length > 0 ? (
              <div className='table-responsive scroll-sm'>
                <table className='table bordered-table sm-table mb-0'>
                  <thead>
                    <tr>
                      <th scope='col'>S.L</th>
                      <th scope='col'>Asset ID</th>
                      <th scope='col'>Brand / Model</th>
                      <th scope='col'>Category</th>
                      <th scope='col'>Serial Number</th>
                      <th scope='col'>Department</th>
                      <th scope='col'>Location</th>
                      <th scope='col' className='text-center'>Condition</th>
                      <th scope='col'>Date Acquired</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.latestAssets.map((asset, index) => (
                      <tr key={asset.id}>
                        <td>{index + 1}</td>
                        <td>
                          <span className='badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm'>
                            {asset.asset_id}
                          </span>
                        </td>
                        <td>{asset.brand_model || 'N/A'}</td>
                        <td>{asset.category || 'N/A'}</td>
                        <td>{asset.serial_number || 'N/A'}</td>
                        <td>{asset.department || 'N/A'}</td>
                        <td>{asset.location || 'N/A'}</td>
                        <td className='text-center'>
                          <span
                            className={`${getConditionBadge(
                              asset.condition
                            )} px-24 py-4 radius-4 fw-medium text-sm text-capitalize`}
                          >
                            {asset.condition || 'N/A'}
                          </span>
                        </td>
                        <td>{formatDate(asset.date_acquired)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='text-center py-5 text-secondary-light'>No IT asset data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItAssetDashboardLayer;

